/**
 * Менеджер управления видом боя (модальное окно, рендеринг)
 * Вынесено из legacy-app.js для модульности
 * 
 * Управляет модальным окном боя, рендерингом панелей, стадии, прогресса и лога
 */

/**
 * @typedef {Object} BattleViewManager
 * @property {Function} showBattleModal - Показывает модальное окно боя
 * @property {Function} hideBattleModal - Скрывает модальное окно боя
 * @property {Function} clearBattleCountdown - Очищает countdown
 * @property {Function} stopBattleTimer - Останавливает таймер боя
 * @property {Function} renderBattleView - Рендерит вид боя
 * @property {Function} resetBattleView - Сбрасывает вид боя
 * @property {Function} renderPanels - Рендерит панели бойцов
 * @property {Function} renderStage - Рендерит стадию боя
 * @property {Function} renderProgress - Рендерит прогресс боя
 * @property {Function} renderLog - Рендерит лог боя
 * @property {Function} renderPlaybackStage - Рендерит стадию playback
 * @property {Function} renderPlaybackPanels - Рендерит панели playback
 * @property {Function} renderPlaybackLog - Рендерит лог playback
 */

/**
 * Создает менеджер управления видом боя
 * 
 * @param {Object} params - Параметры менеджера
 * @param {BattleState} params.battleState - Состояние боя (w)
 * @param {Function} params.hideBattleWinnerBanner - Функция скрытия баннера победителя (Be)
 * @param {Function} params.renderBattlePanels - Функция рендеринга панелей (po/qn)
 * @param {Function} params.renderBattleStage - Функция рендеринга стадии (mo/Fn)
 * @param {Function} params.renderBattleProgress - Функция рендеринга прогресса (fo/We)
 * @param {Function} params.renderBattleLog - Функция рендеринга лога (ho/Gn)
 * @param {Function} params.renderBattlePlaybackPanels - Функция рендеринга панелей playback (Wn)
 * @param {Function} params.renderBattlePlaybackStage - Функция рендеринга стадии playback (_e)
 * @param {Function} params.renderBattlePlaybackLog - Функция рендеринга лога playback (Pe)
 * @param {Function} params.clearBattleRenderCache - Функция очистки кэша рендеринга
 * @param {Function} [params.onBattleRenderComplete] - Колбэк после рендеринга (Kn)
 * @returns {BattleViewManager} Объект с методами менеджера
 * 
 * @example
 * const viewManager = createBattleViewManager({
 *   battleState: w,
 *   hideBattleWinnerBanner: Be,
 *   // ... другие параметры
 * });
 * viewManager.showBattleModal();
 */
export function createBattleViewManager({
  battleState,
  hideBattleWinnerBanner,
  renderBattlePanels,
  renderBattleStage,
  renderBattleProgress,
  renderBattleLog,
  renderBattlePlaybackPanels,
  renderBattlePlaybackStage,
  renderBattlePlaybackLog,
  clearBattleRenderCache,
  onBattleRenderComplete,
}) {
  /**
   * Показывает модальное окно боя
   * 
   * Добавляет классы для отображения модального окна и playback панели.
   * 
   * @returns {void}
   */
  function showBattleModal() {
    const modal = document.getElementById("battle-modal");
    const playback = document.getElementById("battle-playback");
    
    if (modal) {
      if (!modal.classList.contains("active")) {
        modal.classList.add("active");
        document.body.classList.add("battle-modal-open");
      }
    }
    
    if (playback && battleState && battleState.timeline && battleState.timeline.length > 0) {
      playback.classList.add("active");
    }
  }

  /**
   * Скрывает модальное окно боя
   * 
   * @param {Object} [options={}] - Опции
   * @param {boolean} [options.reset=true] - Полный сброс состояния боя
   * @returns {void}
   */
  function hideBattleModal({ reset = true } = {}) {
    const modal = document.getElementById("battle-modal");
    const playback = document.getElementById("battle-playback");
    
    if (modal && modal.classList.contains("active")) {
      modal.classList.remove("active");
      document.body.classList.remove("battle-modal-open");
    }
    
    if (playback) {
      playback.classList.remove("active");
    }
    
    if (reset) {
      resetBattleView();
    } else {
      stopBattleTimer();
      clearBattleCountdown();
    }
  }

  /**
   * Очищает таймеры и countdown боя
   * 
   * Очищает все таймеры из battleState.autoTimeouts,
   * скрывает countdown элемент и баннер победителя.
   * 
   * @returns {void}
   */
  function clearBattleCountdown() {
    if (battleState.autoTimeouts && battleState.autoTimeouts.length > 0) {
      battleState.autoTimeouts.forEach((timeout) => clearTimeout(timeout));
      battleState.autoTimeouts = [];
    }
    
    const countdownEl = document.getElementById("battle-countdown");
    if (countdownEl) {
      countdownEl.style.display = "none";
      const textEl = countdownEl.querySelector(".battle-countdown-text");
      if (textEl) {
        textEl.textContent = "";
      }
      countdownEl.classList.remove(
        "battle-countdown--large",
        "battle-countdown--small",
        "battle-countdown--fatal"
      );
    }
    
    hideBattleWinnerBanner();
  }

  /**
   * Останавливает таймер боя
   * 
   * Очищает таймер автоматического воспроизведения боя.
   * 
   * @returns {void}
   */
  function stopBattleTimer() {
    if (battleState.timer) {
      clearTimeout(battleState.timer);
      battleState.timer = null;
    }
  }

  /**
   * Рендерит весь вид боя
   * 
   * Вызывает функции рендеринга панелей, стадии, прогресса и лога.
   * После завершения вызывает колбэк onBattleRenderComplete, если он задан.
   * 
   * @returns {void}
   */
  function renderBattleView() {
    if (!battleState || !battleState.panels || battleState.panels.length === 0) {
      console.warn('[renderBattleView] No battle data to render');
      return;
    }
    
    try {
      renderBattlePanels(battleState);
      renderBattleStage(battleState);
      renderBattleProgress(battleState);
      renderBattleLog(battleState);
      
      if (typeof onBattleRenderComplete === 'function') {
        onBattleRenderComplete();
      }
    } catch (error) {
      console.error('[renderBattleView] Error rendering battle:', error);
    }
  }

  /**
   * Полностью сбрасывает состояние боя
   * 
   * Останавливает таймеры, очищает countdown и сбрасывает
   * все данные боя в начальное состояние.
   * 
   * @returns {void}
   */
  function resetBattleView() {
    stopBattleTimer();
    clearBattleCountdown();
    
    // Очищаем состояние боя
    battleState.battle = null;
    battleState.panels = [];
    battleState.timeline = [];
    battleState.currentStep = 0;
    battleState.isPlaying = false;
    battleState.lastRenderedStep = -1;
    battleState.winner = null;
    battleState.summary = null;
    battleState.telemetryByRound = {};
    
    hideBattleWinnerBanner();
    
    // Очищаем кэш рендеринга
    try {
      if (typeof clearBattleRenderCache === 'function') {
        clearBattleRenderCache();
      }
    } catch (error) {
      console.warn('[resetBattleView] Failed to clear render cache:', error);
    }
    
    // Не вызываем renderBattleView() после очистки данных - это вызовет ошибку
    // renderBattle будет вызван позже, когда данные будут загружены
  }

  /**
   * Рендерит панели боя
   * 
   * Обертка над renderBattlePanels для удобства использования.
   * 
   * @returns {void}
   */
  function renderPanels() {
    renderBattlePanels(battleState);
  }

  /**
   * Рендерит стадию боя
   * 
   * Обертка над renderBattleStage для удобства использования.
   * 
   * @returns {void}
   */
  function renderStage() {
    renderBattleStage(battleState);
  }

  /**
   * Рендерит прогресс боя
   * 
   * Обертка над renderBattleProgress для удобства использования.
   * 
   * @returns {void}
   */
  function renderProgress() {
    renderBattleProgress(battleState);
  }

  /**
   * Рендерит лог боя
   * 
   * Обертка над renderBattleLog для удобства использования.
   * 
   * @returns {void}
   */
  function renderLog() {
    renderBattleLog(battleState);
  }

  /**
   * Рендерит стадию playback (обертка для _e)
   * @param {number} delay - Задержка (мс)
   */
  function renderPlaybackStage(delay = 600) {
    renderBattlePlaybackStage(battleState);
  }

  /**
   * Рендерит панели playback (обертка для Wn)
   */
  function renderPlaybackPanels() {
    renderBattlePlaybackPanels(battleState);
  }

  /**
   * Рендерит лог playback (обертка для Pe)
   */
  function renderPlaybackLog() {
    renderBattlePlaybackLog(battleState);
  }

  return {
    showBattleModal,
    hideBattleModal,
    clearBattleCountdown,
    stopBattleTimer,
    renderBattleView,
    resetBattleView,
    renderPanels,
    renderStage,
    renderProgress,
    renderLog,
    renderPlaybackStage,
    renderPlaybackPanels,
    renderPlaybackLog,
  };
}

